import { useInView } from "@/hooks/useAnimations";
import { BookOpen, Clock, ArrowRight, Calculator } from "lucide-react";
import { Link } from "react-router-dom";

const articles = [
  {
    slug: "sozialraumbudget-pflege",
    tag: "Sozialraumbudget",
    title: "Sozialraumbudget in der Pflege: Was Sie wissen sollten",
    excerpt: "Was bedeutet das geplante Sozialraumbudget? Einfach erklärt für Pflegebedürftige und Angehörige — inklusive FAQ und Praxisbeispiele.",
    readTime: "12 Min.",
    color: "bg-cyan-50",
    textColor: "text-cyan-700",
    featured: true,
  },
  {
    slug: "sozialraumbudget-2027",
    tag: "Pflegereform",
    title: "Sozialraumbudget 2027: Was ist bisher bekannt?",
    excerpt: "Aktueller Diskussionsstand, mögliche Zeitrahmen und was Angehörige jetzt schon wissen sollten.",
    readTime: "7 Min.",
    color: "bg-green-50",
    textColor: "text-green-600",
  },
  {
    slug: "sozialraumbudget-entlastungsbetrag",
    tag: "Entlastungsbetrag",
    title: "Sozialraumbudget oder Entlastungsbetrag: Was ist der Unterschied?",
    excerpt: "Zwei Instrumente für Alltagsunterstützung — wir erklären die Unterschiede verständlich mit Beispielen.",
    readTime: "6 Min.",
    color: "bg-cyan-50",
    textColor: "text-cyan-700",
  },
  {
    slug: "pflegereform-2027",
    tag: "Pflegereform",
    title: "Pflegereform 2027: Was Angehörige wissen sollten",
    excerpt: "Überblick über mögliche Änderungen im Pflegesystem — von neuen Leistungen bis zu vereinfachten Anträgen.",
    readTime: "7 Min.",
    color: "bg-green-50",
    textColor: "text-green-600",
  },
  {
    isRechner: true,
    slug: "sozialraumbudget-rechner",
    tag: "Interaktiv",
    title: "Sozialraumbudget-Rechner",
    excerpt: "Unverbindliche Simulation: Prüfen Sie, welche Auswirkungen diskutierte Reformen auf Ihre Situation haben könnten.",
    readTime: "3 Min.",
    color: "bg-amber-50",
    textColor: "text-amber-700",
  },
];

export default function Ratgeber() {
  const [ref, isInView] = useInView();

  return (
    <section
      ref={ref}
      id="ratgeber"
      data-testid="ratgeber-section"
      className="py-20 lg:py-28 bg-white"
      aria-label="Pflege-Ratgeber und Wissenswertes"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-14 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-green-600 bg-green-50 px-4 py-1.5 rounded-full mb-4">
            <BookOpen className="h-3.5 w-3.5" />
            Pflege-Ratgeber
          </span>
          <h2 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Wissen rund um die Pflege
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            Verständliche Fachartikel zu Pflegeleistungen, Sozialraumbudget, Entlastungsbetrag und mehr.
          </p>
        </div>

        {/* Article cards */}
        <div className="grid sm:grid-cols-2 gap-6">
          {articles.map((a, i) => (
            <Link
              to={`/blog/${a.slug}`}
              key={i}
              data-testid={`ratgeber-card-${i}`}
              className={`bg-slate-50/50 border border-slate-200/60 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 group cursor-pointer ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${a.featured ? 'sm:col-span-2 border-cyan-200 bg-cyan-50/30' : ''} ${a.isRechner ? 'border-amber-200 bg-amber-50/30' : ''}`}
              style={{ transitionDelay: isInView ? `${i * 120}ms` : '0ms' }}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-xs font-semibold ${a.textColor} ${a.color} px-3 py-1 rounded-full`}>
                  {a.isRechner && <Calculator className="h-3 w-3 inline mr-1" />}
                  {a.tag}
                </span>
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="h-3 w-3" />
                  {a.readTime}
                </span>
                {a.featured && <span className="text-xs font-medium text-white bg-cyan-700 px-2.5 py-1 rounded-full">Hauptartikel</span>}
                {a.isRechner && <span className="text-xs font-medium text-white bg-amber-600 px-2.5 py-1 rounded-full">Neu</span>}
              </div>
              <h3 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-2 group-hover:text-cyan-700 transition-colors">
                {a.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed mb-3">
                {a.excerpt}
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 group-hover:gap-2 transition-all">
                {a.isRechner ? "Zum Rechner" : "Artikel lesen"} <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>

        {/* All articles link */}
        <div className={`text-center mt-8 transition-all duration-700 delay-300 ${isInView ? 'opacity-100' : 'opacity-0'}`}>
          <Link to="/blog" className="inline-flex items-center gap-2 text-base font-semibold text-cyan-700 hover:text-cyan-800 hover:gap-3 transition-all">
            Alle Ratgeber-Artikel <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
